import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const CardSecurityControls = ({ cardStatus }) => {
  const [dailyLimit, setDailyLimit] = useState(1000);
  const [monthlyLimit, setMonthlyLimit] = useState(5000);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState('worldwide');

  const regionOptions = [
    { value: 'worldwide', label: 'Worldwide' },
    { value: 'north-america', label: 'North America' },
    { value: 'europe', label: 'Europe' },
    { value: 'asia', label: 'Asia' },
    { value: 'custom', label: 'Custom Regions' }
  ];

  const categoryLimits = [
    { category: 'Shopping', limit: 500, icon: 'ShoppingBag', color: '#3B82F6' },
    { category: 'Food & Dining', limit: 300, icon: 'Coffee', color: '#10B981' },
    { category: 'Transportation', limit: 200, icon: 'Car', color: '#F59E0B' },
    { category: 'Entertainment', limit: 150, icon: 'Tv', color: '#8B5CF6' }
  ];

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title">Security Controls</h2>
          <p className="card-description">Manage card security and spending limits</p>
        </div>
      </div>
      <div className="card-content space-y-6">
        {/* Spending Limits */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="DollarSign" size={16} className="text-primary" />
            Spending Limits
          </h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Daily Limit</label>
                <Input 
                  type="number" 
                  value={dailyLimit}
                  onChange={(e) => setDailyLimit(e?.target?.value)}
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Monthly Limit</label>
                <Input 
                  type="number" 
                  value={monthlyLimit}
                  onChange={(e) => setMonthlyLimit(e?.target?.value)}
                  className="text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Category Limits */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="Tag" size={16} className="text-primary" />
            Category Limits
          </h3>
          <div className="space-y-2">
            {categoryLimits?.map((item) => (
              <div key={item?.category} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Icon name={item?.icon} size={16} style={{ color: item?.color }} />
                  <span className="text-sm text-foreground">{item?.category}</span>
                </div>
                <span className="text-sm font-semibold text-foreground">${item?.limit}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Geographic Restrictions */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="MapPin" size={16} className="text-primary" />
            Geographic Restrictions
          </h3>
          <Select
            options={regionOptions}
            value={selectedRegion}
            onChange={setSelectedRegion}
            placeholder="Select allowed regions"
          />
        </div>

        {/* Security Features */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="Shield" size={16} className="text-primary" />
            Security Features
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="Bell" size={16} className="text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">Transaction Notifications</p>
                  <p className="text-xs text-muted-foreground">Get alerts for all transactions</p>
                </div>
              </div>
              <button
                onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  notificationsEnabled ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    notificationsEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="Lock" size={16} className="text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">Two-Factor Authentication</p>
                  <p className="text-xs text-muted-foreground">Require 2FA for sensitive changes</p>
                </div>
              </div>
              <button
                onClick={() => setTwoFactorEnabled(!twoFactorEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  twoFactorEnabled ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        <Button variant="default" fullWidth iconName="Save">
          Save Security Settings
        </Button>
      </div>
    </div>
  );
};

export default CardSecurityControls;